import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { apiClient } from '@/lib/api-client'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ArrowLeft, Lock, Unlock } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'

interface UserDetail {
  id: string
  email: string
  name: string
  image: string
  plan: string
  role: string
  status: string
  authProvider: string
  githubLogin: string
  createdAt: string
  totalNetworks: number
  totalValidations: number
  lastActiveAt?: string
}

interface SubscriptionChange {
  id: string
  userId: string
  requestedById: string
  previousPlan: string
  newPlan: string
  reason: string
  effectiveAt: string
  createdAt: string
}

interface UserNetwork {
  id: string
  name: string
  description?: string
  sourceType: string
  active: boolean
  createdAt: string
  updatedAt: string
}

export default function AdminUserDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { isSuperAdmin } = useAuth()
  const { toast } = useToast()
  const [user, setUser] = useState<UserDetail | null>(null)
  const [history, setHistory] = useState<SubscriptionChange[]>([])
  const [networks, setNetworks] = useState<UserNetwork[]>([])
  const [loading, setLoading] = useState(true)

  const [lockDialogOpen, setLockDialogOpen] = useState(false)
  const [lockReason, setLockReason] = useState('')
  const [planDialogOpen, setPlanDialogOpen] = useState(false)
  const [newPlan, setNewPlan] = useState('')
  const [planReason, setPlanReason] = useState('')
  const [roleDialogOpen, setRoleDialogOpen] = useState(false)
  const [newRole, setNewRole] = useState('')

  useEffect(() => {
    if (!id) return
    const load = async () => {
      const [userResp, historyResp, networksResp] = await Promise.all([
        apiClient.getAdminUser(id),
        apiClient.listSubscriptionHistory({ userId: id }),
        apiClient.listUserNetworks(id),
      ])
      if (userResp.success && userResp.data) {
        setUser(userResp.data as UserDetail)
      }
      if (historyResp.success && historyResp.data) {
        setHistory((historyResp.data as { changes: SubscriptionChange[] }).changes || [])
      }
      if (networksResp.success && networksResp.data) {
        setNetworks((networksResp.data as { networks: UserNetwork[] }).networks || [])
      }
      setLoading(false)
    }
    load()
  }, [id])

  const handleLock = async () => {
    if (!id || !lockReason) return
    const resp = await apiClient.lockUser(id, lockReason)
    if (resp.success) {
      setUser((u) => u ? { ...u, status: 'locked' } : u)
      toast({ title: 'User locked' })
    }
    setLockDialogOpen(false)
    setLockReason('')
  }

  const handleUnlock = async () => {
    if (!id) return
    const resp = await apiClient.unlockUser(id)
    if (resp.success) {
      setUser((u) => u ? { ...u, status: 'active' } : u)
      toast({ title: 'User unlocked' })
    }
  }

  const handlePlanChange = async () => {
    if (!id || !newPlan) return
    const resp = await apiClient.changeUserPlan(id, newPlan, planReason)
    if (resp.success) {
      setUser((u) => u ? { ...u, plan: newPlan } : u)
      toast({ title: 'Plan updated' })
      const historyResp = await apiClient.listSubscriptionHistory({ userId: id })
      if (historyResp.success && historyResp.data) {
        setHistory((historyResp.data as { changes: SubscriptionChange[] }).changes || [])
      }
    }
    setPlanDialogOpen(false)
    setNewPlan('')
    setPlanReason('')
  }

  const handleRoleChange = async () => {
    if (!id || !newRole) return
    const resp = await apiClient.updateUserRole(id, newRole)
    if (resp.success) {
      setUser((u) => u ? { ...u, role: newRole } : u)
      toast({ title: 'Role updated' })
    }
    setRoleDialogOpen(false)
    setNewRole('')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    )
  }

  if (!user) {
    return <div className="py-20 text-center text-muted-foreground">User not found</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/users')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold tracking-tight">{user.name || user.email}</h1>
          <p className="text-sm text-muted-foreground">{user.email}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={user.status === 'active' ? 'outline' : 'destructive'}>{user.status}</Badge>
          {user.status === 'active' ? (
            <Button variant="outline" size="sm" onClick={() => setLockDialogOpen(true)}>
              <Lock className="mr-1 h-3 w-3" /> Lock
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={handleUnlock}>
              <Unlock className="mr-1 h-3 w-3" /> Unlock
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="networks">Networks ({networks.length})</TabsTrigger>
          <TabsTrigger value="subscription">Subscription</TabsTrigger>
          {isSuperAdmin && <TabsTrigger value="permissions">Permissions</TabsTrigger>}
        </TabsList>

        <TabsContent value="profile" className="mt-4">
          <Card className="app-surface">
            <CardContent className="grid gap-4 pt-6 sm:grid-cols-2">
              <div>
                <Label className="text-xs text-muted-foreground">Email</Label>
                <p className="text-sm font-medium">{user.email}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Name</Label>
                <p className="text-sm font-medium">{user.name || '-'}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Auth Provider</Label>
                <p className="text-sm font-medium capitalize">{user.authProvider || '-'}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">GitHub Login</Label>
                <p className="text-sm font-medium">{user.githubLogin || '-'}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Created</Label>
                <p className="text-sm font-medium tabular-nums">{new Date(user.createdAt).toLocaleDateString()}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Last Active</Label>
                <p className="text-sm font-medium tabular-nums">
                  {user.lastActiveAt ? new Date(user.lastActiveAt).toLocaleDateString() : '-'}
                </p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Networks</Label>
                <p className="text-sm font-medium tabular-nums">{user.totalNetworks}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Validations</Label>
                <p className="text-sm font-medium tabular-nums">{user.totalValidations}</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="networks" className="mt-4">
          <Card className="app-surface">
            <CardContent className="pt-6">
              {networks.length > 0 ? (
                <div className="overflow-x-auto"><table className="w-full text-sm min-w-[400px]">
                  <thead>
                    <tr className="border-b text-left text-xs text-muted-foreground">
                      <th className="pb-2">Name</th>
                      <th className="pb-2">Source</th>
                      <th className="pb-2">Status</th>
                      <th className="pb-2">Created</th>
                      <th className="pb-2">Last Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {networks.map((network) => (
                      <tr key={network.id} className="border-b last:border-0">
                        <td className="py-2.5">
                          <div>
                            <span className="font-medium">{network.name}</span>
                            {network.description && (
                              <p className="text-xs text-muted-foreground mt-0.5 max-w-md truncate">{network.description}</p>
                            )}
                          </div>
                        </td>
                        <td className="py-2.5">
                          <Badge variant="outline" className="text-[10px] capitalize">{network.sourceType}</Badge>
                        </td>
                        <td className="py-2.5">
                          <Badge variant={network.active ? 'default' : 'secondary'} className="text-[10px]">
                            {network.active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="py-2.5 text-muted-foreground tabular-nums">
                          {new Date(network.createdAt).toLocaleDateString()}
                        </td>
                        <td className="py-2.5 text-muted-foreground tabular-nums">
                          {new Date(network.updatedAt).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              ) : (
                <p className="text-sm text-muted-foreground">No networks</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="subscription" className="mt-4 space-y-4">
          <Card className="app-surface">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Current Plan</CardTitle>
              <Button variant="outline" size="sm" onClick={() => setPlanDialogOpen(true)}>Change Plan</Button>
            </CardHeader>
            <CardContent>
              <Badge variant="outline" className="capitalize text-sm">{user.plan || 'free'}</Badge>
            </CardContent>
          </Card>

          <Card className="app-surface">
            <CardHeader>
              <CardTitle className="text-base">Plan Change History</CardTitle>
            </CardHeader>
            <CardContent>
              {history.length > 0 ? (
                <div className="overflow-x-auto"><table className="w-full text-sm min-w-[400px]">
                  <thead>
                    <tr className="border-b text-left text-xs text-muted-foreground">
                      <th className="pb-2">Date</th>
                      <th className="pb-2">From</th>
                      <th className="pb-2">To</th>
                      <th className="pb-2">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((change) => (
                      <tr key={change.id} className="border-b last:border-0">
                        <td className="py-2 tabular-nums">{new Date(change.createdAt).toLocaleDateString()}</td>
                        <td className="py-2"><Badge variant="outline" className="text-[10px] capitalize">{change.previousPlan}</Badge></td>
                        <td className="py-2"><Badge variant="outline" className="text-[10px] capitalize">{change.newPlan}</Badge></td>
                        <td className="py-2 text-muted-foreground">{change.reason || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              ) : (
                <p className="text-sm text-muted-foreground">No plan changes</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {isSuperAdmin && (
          <TabsContent value="permissions" className="mt-4">
            <Card className="app-surface">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">Role</CardTitle>
                <Button variant="outline" size="sm" onClick={() => { setNewRole(user.role); setRoleDialogOpen(true) }}>
                  Change Role
                </Button>
              </CardHeader>
              <CardContent>
                <Badge variant={user.role === 'superadmin' ? 'default' : user.role === 'admin' ? 'secondary' : 'outline'} className="capitalize">
                  {user.role || 'user'}
                </Badge>
                <p className="mt-2 text-xs text-muted-foreground">
                  Only superadmins can change user roles. Admins can manage users and billing. Superadmins can additionally manage roles.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Lock Dialog */}
      <Dialog open={lockDialogOpen} onOpenChange={setLockDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Lock User Account</DialogTitle>
            <DialogDescription>This will prevent the user from logging in.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Reason</Label>
            <Textarea value={lockReason} onChange={(e) => setLockReason(e.target.value)} placeholder="Why is this account being locked?" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLockDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleLock} disabled={!lockReason}>Lock Account</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Plan Change Dialog */}
      <Dialog open={planDialogOpen} onOpenChange={setPlanDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Subscription Plan</DialogTitle>
            <DialogDescription>Change this user's subscription plan.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>New Plan</Label>
              <Select value={newPlan} onValueChange={setNewPlan}>
                <SelectTrigger><SelectValue placeholder="Select plan" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reason</Label>
              <Input value={planReason} onChange={(e) => setPlanReason(e.target.value)} placeholder="Reason for change" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanDialogOpen(false)}>Cancel</Button>
            <Button onClick={handlePlanChange} disabled={!newPlan}>Update Plan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role Change Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change User Role</DialogTitle>
            <DialogDescription>Changing roles affects what the user can access.</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Role</Label>
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="superadmin">Superadmin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleRoleChange} disabled={!newRole || newRole === user.role}>Update Role</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
